import type {
  TrimFeatureAssignment,
  TrimFeatureAssignmentInput,
  TrimResponseBody,
} from '@dms/api/new-vehicle';

import type {PricedItemBatchEntry} from '../types/PricedItemBatchEntry';
import type {PricedItemChange} from '../types/PricedItemChange';
import {assignmentReplaceOperation} from './assignmentReplaceOperation';
import {pricedItemGroupKeys} from './pricedItemGroupKeys';

/**
 * The equipment write of one trim as a batch entry, keyed `trim:<trimId>` (one write per trim in a
 * batch, `duplicateTarget`): one `trimFeatureAssignment.replace`. Sent at once, it has the trim
 * version read for `next`; a trim changed meanwhile fails the batch as stale (DMS-NV-CT-0012). In an
 * open group it is built at Confirm: it reads the trim version then, and sends only while the trim's
 * assignments are still `stored`, the ones `next` was made from (assignmentReplaceOperation).
 */
export const trimAssignmentEntry = (options: {
  trim: Pick<TrimResponseBody, 'id' | 'version'>;
  title: string;
  items: PricedItemChange[];
  /** The assignments `next` was made from. */
  stored: TrimFeatureAssignment[];
  next: TrimFeatureAssignmentInput[];
  readTrimVersion: () => Promise<number>;
  readAssignments: () => Promise<TrimFeatureAssignment[]>;
}): PricedItemBatchEntry => ({
  key: pricedItemGroupKeys.trim(options.trim.id),
  title: options.title,
  items: options.items,
  build: async () => ({
    items: options.items,
    operations: [
      await assignmentReplaceOperation({
        trimId: options.trim.id,
        base: options.stored,
        next: options.next,
        readTrimVersion: options.readTrimVersion,
        readAssignments: options.readAssignments,
      }),
    ],
  }),
  buildAtOnce: () =>
    Promise.resolve({
      items: options.items,
      operations: [
        {
          type: 'trimFeatureAssignment.replace',
          targetId: options.trim.id,
          body: {version: options.trim.version, assignments: options.next},
        },
      ],
    }),
});
